import { useRef, useEffect, useCallback } from 'react';

interface ScrollPickerProps {
  items: string[];
  selectedIndex: number;
  onChange: (index: number) => void;
  itemHeight?: number;
  visibleCount?: number;
}

export default function ScrollPicker({
  items,
  selectedIndex,
  onChange,
  itemHeight = 52,
  visibleCount = 5,
}: ScrollPickerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isScrolling = useRef(false);
  const scrollTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scrollToIndex = useCallback((index: number, smooth = true) => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTo({ top: index * itemHeight, behavior: smooth ? 'smooth' : 'auto' });
  }, [itemHeight]);

  useEffect(() => {
    scrollToIndex(selectedIndex, false);
  }, []);

  useEffect(() => {
    if (!isScrolling.current) {
      scrollToIndex(selectedIndex, true);
    }
  }, [selectedIndex, scrollToIndex]);

  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    isScrolling.current = true;
    if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    scrollTimeout.current = setTimeout(() => {
      isScrolling.current = false;
      const rawIndex = el.scrollTop / itemHeight;
      const snappedIndex = Math.round(rawIndex);
      const clamped = Math.max(0, Math.min(snappedIndex, items.length - 1));
      scrollToIndex(clamped, true);
      if (clamped !== selectedIndex) onChange(clamped);
    }, 150);
  };

  const containerHeight = itemHeight * visibleCount;
  const padItems = Math.floor(visibleCount / 2);

  return (
    <div className="relative select-none overflow-hidden rounded-xl" style={{ height: containerHeight }}>

      {/* ① 하이라이트 박스 - 가장 아래(z-0) */}
      <div
        className="absolute inset-x-0 pointer-events-none rounded-xl bg-gray-100"
        style={{ top: itemHeight * padItems, height: itemHeight, zIndex: 0 }}
      />

      {/* ② 스크롤 영역 - 중간(z-10), 글자가 하이라이트 위에 보임 */}
      <div
        ref={containerRef}
        className="absolute inset-0 overflow-y-scroll"
        style={{ scrollbarWidth: 'none', zIndex: 10 }}
        onScroll={handleScroll}
      >
        {Array.from({ length: padItems }).map((_, i) => (
          <div key={`pad-top-${i}`} style={{ height: itemHeight }} />
        ))}
        {items.map((item, idx) => {
          const distance = Math.abs(idx - selectedIndex);
          const opacity = distance === 0 ? 1 : distance === 1 ? 0.55 : 0.22;
          const fontSize = distance === 0 ? '1.15rem' : '1rem';
          const fontWeight = distance === 0 ? '700' : '400';
          return (
            <div
              key={idx}
              className="flex items-center justify-center cursor-pointer transition-all duration-150"
              style={{ height: itemHeight, opacity, fontSize, fontWeight, color: '#0F0F0F' }}
              onClick={() => { onChange(idx); scrollToIndex(idx); }}
            >
              {item}
            </div>
          );
        })}
        {Array.from({ length: padItems }).map((_, i) => (
          <div key={`pad-bot-${i}`} style={{ height: itemHeight }} />
        ))}
      </div>

      {/* ③ 그라디언트 페이드 - 가장 위(z-20), 위아래 흐림 효과 */}
      <div
        className="absolute inset-x-0 top-0 pointer-events-none"
        style={{
          height: itemHeight * padItems,
          background: 'linear-gradient(to bottom, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)',
          zIndex: 20,
        }}
      />
      <div
        className="absolute inset-x-0 bottom-0 pointer-events-none"
        style={{
          height: itemHeight * padItems,
          background: 'linear-gradient(to top, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)',
          zIndex: 20,
        }}
      />
    </div>
  );
}
